import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { MonthlyBarChart, PwaInstallHint } from '@kilat/ui';

import { useAgentId, useAuth, useRecruiterId } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { LeaderboardTile } from '../components/leaderboard-tile.js';
import { AcademyProgressCard } from '../components/academy-progress-card.js';
import { WarmLeadsCard } from '../components/warm-leads-card.js';
import {
  earningsLedgerRepoFor,
  operatorsBackrefRepoFor,
} from '../lib/repositories/earnings-repo.js';
import { accruedAtMs, describeLedgerEntry, recentActivity } from '../lib/ledger-activity.js';
import {
  computeMilestones,
  earningsStreakMonths,
  forecastNextMonthSen,
  isRecruiterChannelEnabled,
  money,
  paidOutSen,
  payableBalanceSen,
  summariseLedgerByMonth,
  summariseLedgerTotals,
  unpaidBalanceSen,
  type AgentOperatorBackref,
  type EarningsLedgerEntry,
  type Milestone,
  type NextMonthForecast,
} from '@kilat/shared';

/**
 * /home — agent dashboard (Sprint 95: one-time commission model).
 *
 * Three motivational pieces above the surface CTAs:
 *   1. Signup-velocity hero — answers "if I keep signing operators at
 *      my recent pace, how much will I earn next month?" based on the
 *      trailing-3-month average of signup commissions. Frames it as
 *      velocity, never as guaranteed recurring income.
 *   2. Last-six-months stacked bar chart — signup commissions on
 *      bottom, first-invoice bonuses on top. Empty months render as a
 *      faint baseline so a slow month still reads as data.
 *   3. Milestone strip — real-event-triggered (only flips on actual
 *      ledger / backref state). Progress ring on the next pending one.
 *
 * No-dead-ends: when the agent has no history yet, the hero card
 * explicitly says "Your first signup unlocks your first commission"
 * instead of rendering a confusing zero.
 */
export function HomePage() {
  // Auth-gating lives in `App`'s `AuthGate`: this route only renders once a user
  // is signed in (loading + signed-out are the sign-in screen's job now), so we
  // no longer branch on `loading` / `!user` here. What remains is the
  // signed-in-but-no-agent-claim resolution below.
  const { user, signOut } = useAuth();
  const agentId = useAgentId();
  const rawRecruiterId = useRecruiterId();
  const brand = usePlatformBrand();
  const t = useI18n();
  // ADR-0023: the recruiter persona only exists here when the brand's recruiter
  // channel is enabled. When off, a recruiter claim is ignored so we never link
  // into (or redirect to) the gated `/recruiter/*` routes.
  const recruiterChannelOn = isRecruiterChannelEnabled(brand);
  const recruiterId = rawRecruiterId && recruiterChannelOn ? rawRecruiterId : null;

  if (!agentId) {
    if (recruiterId) {
      return <Navigate to="/recruiter" replace />;
    }
    // A recruiter-only account on a brand where the channel is paused: don't
    // dead-end them into a redirect loop or an "apply to recruit" link that
    // no longer works — tell them plainly it's paused.
    if (rawRecruiterId && !recruiterChannelOn) {
      return (
        <Wrap>
          <h1 style={{ margin: 0, fontSize: 'var(--text-h1)', fontWeight: 600 }}>
            {brand.shortName}
          </h1>
          <p style={{ marginTop: 'var(--space-2)', color: 'var(--color-on-surface-muted)' }}>
            {t.t('agent.home.recruiterPausedBody')}{' '}
            <a href={`mailto:${brand.contact.email}`}>{brand.contact.email}</a>.
          </p>
        </Wrap>
      );
    }
    // Signed in, but this account carries no agent (or recruiter) claim — e.g. a
    // brand-new Google user who never applied, or someone who signed in with the
    // wrong account. Mirror the dashboard's NoOperatorAccount: name the account
    // they're on, give the real next action (apply), AND a sign-out escape hatch
    // so they can switch to the email they actually registered with — never a
    // dead-end ([[feedback-no-dead-ends]]).
    return (
      <Wrap>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h1)', fontWeight: 600 }}>
          {brand.shortName}
        </h1>
        <p style={{ marginTop: 'var(--space-2)', color: 'var(--color-on-surface-muted)' }}>
          {recruiterChannelOn
            ? t.t('agent.home.notAgentLeadWithRecruiter')
            : t.t('agent.home.notAgentLead')}{' '}
          <a href={`https://${brand.domains.marketing}/become-an-agent`}>
            {t.t('agent.home.becomeAnAgentLink')}
          </a>
          {recruiterChannelOn ? (
            <>
              {' '}
              {t.t('agent.home.orConnector')}{' '}
              <a href={`https://${brand.domains.marketing}/become-a-recruiter`}>
                {t.t('agent.home.becomeARecruiterLink')}
              </a>
            </>
          ) : null}
          .
        </p>
        {user?.email ? (
          <p
            style={{
              marginTop: 'var(--space-3)',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t.t('agent.home.signedInAs', { email: user.email })}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => void signOut()}
          style={{
            marginTop: 'var(--space-3)',
            alignSelf: 'flex-start',
            background: 'transparent',
            border: '1px solid var(--color-border-strong)',
            borderRadius: 'var(--radius-pill)',
            padding: '0.5em 1em',
            minHeight: 'var(--touch-target)',
            color: 'var(--color-on-surface)',
            fontSize: 'var(--text-meta)',
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          {t.t('agent.home.notAgentSwitch')}
        </button>
      </Wrap>
    );
  }

  return (
    <AgentHomeAuthenticated
      agentId={agentId}
      recruiterId={recruiterId}
      brandName={brand.name}
      brandShortName={brand.shortName}
    />
  );
}

function AgentHomeAuthenticated({
  agentId,
  recruiterId,
  brandName,
  brandShortName,
}: {
  readonly agentId: string;
  readonly recruiterId: string | null;
  readonly brandName: string;
  readonly brandShortName: string;
}) {
  const t = useI18n();
  // Per-listener error state (NOT one shared flag): the ledger and operators
  // listeners each own their error so a healthy listener's success can't clear
  // the other's. Critically, a denied/dropped read on EITHER feed must surface
  // as an explicit error+retry — never a silent RM 0.00 / zero stats on the
  // money-woven hero, chart, and milestones (per [[feedback-no-dead-ends]];
  // same class as recruiter-home #68). `loadError` is their union for the gate.
  const [ledgerError, setLedgerError] = useState<Error | null>(null);
  const [operatorsError, setOperatorsError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const loadError = ledgerError ?? operatorsError;
  const retryAll = () => {
    setLedgerError(null);
    setOperatorsError(null);
    setRetryNonce((n) => n + 1);
  };

  const ledgerSub = useMemo(() => {
    const repo = earningsLedgerRepoFor(agentId);
    return (cb: (vs: EarningsLedgerEntry[]) => void) =>
      repo.listSnapshot(
        (v) => {
          setLedgerError(null);
          cb(v);
        },
        (err) => setLedgerError(err),
      );
  }, [agentId]);
  const { data: ledger } = useTrackedSnapshot<EarningsLedgerEntry[]>(
    ['agent-home-ledger', agentId, retryNonce],
    ledgerSub,
  );

  const operatorsSub = useMemo(() => {
    const repo = operatorsBackrefRepoFor(agentId);
    return (cb: (vs: AgentOperatorBackref[]) => void) =>
      repo.listSnapshot(
        (v) => {
          setOperatorsError(null);
          cb(v);
        },
        (err) => setOperatorsError(err),
      );
  }, [agentId]);
  const { data: operators } = useTrackedSnapshot<AgentOperatorBackref[]>(
    ['agent-home-ops', agentId, retryNonce],
    operatorsSub,
  );

  const nowMs = Date.now();
  const entries = useMemo(() => ledger ?? [], [ledger]);
  const buckets = useMemo(() => summariseLedgerByMonth(entries, nowMs, 6), [entries, nowMs]);
  const forecast = useMemo(() => forecastNextMonthSen(buckets, nowMs, 3), [buckets, nowMs]);
  const streak = useMemo(() => earningsStreakMonths(buckets, nowMs), [buckets, nowMs]);
  // Lifetime earnings (excludes payout debits) via the shipped helper — the
  // same value the old inline `filter(kind !== 'paid').reduce(...)` produced,
  // now sharing one tested home with the balance tile. Milestones still read it.
  const lifetimeEarningsSen = useMemo(() => summariseLedgerTotals(entries).net, [entries]);
  // Money position for the balance tile — all three from the shipped, tested
  // ledger-summary helpers on the already-loaded ledger (no new listener).
  const owedSen = useMemo(() => unpaidBalanceSen(entries), [entries]);
  const payableSen = useMemo(() => payableBalanceSen(entries, nowMs), [entries, nowMs]);
  const paidSen = useMemo(() => paidOutSen(entries), [entries]);
  // Recent activity feed rows: newest-first, capped, via the pure helper.
  const activity = useMemo(() => recentActivity(entries, 8), [entries]);
  const ops = operators ?? [];
  const activeOpCount = ops.filter((o) => o.currentStatus === 'active').length;
  const lifetimeOpCount = ops.length;
  // tenantId → operator display name, so an accrual row can name the operator
  // it came from. Keyed by both the back-ref doc id and its `tenantId` mirror
  // (either can match a ledger entry's `tenantId`). No name on file → unlabelled.
  const operatorNameByTenant = useMemo(() => {
    const byTenant = new Map<string, string>();
    for (const o of operators ?? []) {
      const name = o.tenantBusinessName;
      if (!name) continue;
      byTenant.set(o.id, name);
      if (o.tenantId) byTenant.set(o.tenantId, name);
    }
    return byTenant;
  }, [operators]);
  const milestones = useMemo(
    () =>
      computeMilestones({
        activeOperatorCount: activeOpCount,
        lifetimeOperatorCount: lifetimeOpCount,
        lifetimeEarningsSen,
        earningsStreakMonths: streak,
      }),
    [activeOpCount, lifetimeOpCount, lifetimeEarningsSen, streak],
  );

  // Money-safety gate: a failed ledger/operators read renders an explicit
  // error + retry instead of a misleading zero across the hero/chart/milestones.
  if (loadError) {
    return (
      <Wrap>
        <section style={{ ...cardStyle, textAlign: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>
            {t.t('agent.loadError.homeTitle')}
          </h2>
          <p
            style={{
              margin: 'var(--space-3) auto 0 auto',
              maxWidth: 460,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              lineHeight: 1.5,
            }}
          >
            {t.t('agent.loadError.body')}
          </p>
          <button
            type="button"
            onClick={retryAll}
            style={{
              ...primaryCta,
              marginTop: 'var(--space-4)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {t.t('agent.loadError.retry')}
          </button>
        </section>
      </Wrap>
    );
  }

  const agentAppName = t.t('agent.home.agentAppName', { brand: brandShortName });

  return (
    <Wrap dash>
      <header>
        <p style={metaEyebrow}>{t.t('agent.home.eyebrow', { brand: brandName })}</p>
        <h1 style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--text-h1)', fontWeight: 600 }}>
          {t.t('agent.home.welcomeBack')}
        </h1>
      </header>

      <ForecastCard
        forecast={forecast}
        activeOpCount={activeOpCount}
        streak={streak}
        lifetimeOpCount={lifetimeOpCount}
      />

      <BalanceTile
        owedSen={owedSen}
        payableSen={payableSen}
        paidSen={paidSen}
        lifetimeSen={lifetimeEarningsSen}
        isEmpty={entries.length === 0}
      />

      {/* Training ABOVE earnings while the agent is uncertified. The position is
          the feature: the first thing a new agent sees should move when THEY do
          the work, not sit at zero until an operator says yes. It removes itself
          once certified. */}
      <AcademyProgressCard />

      {/* Warm leads first: for a new agent every other card on this screen is
          a zero, and this is the one that can have something in it on day one. */}
      <WarmLeadsCard />

      {/* Last 6 months chart */}
      <section style={cardStyle}>
        <header style={cardHeaderRow}>
          <h2 style={cardTitle}>{t.t('agent.home.last6Months')}</h2>
          <span style={cardHint}>{t.t('agent.home.chartHint')}</span>
        </header>
        <MonthlyBarChart
          buckets={buckets}
          formatMoney={(s) => money.format(s)}
          tone="agent"
          emptyLabel={t.t('chart.emptyLabel')}
          emptyBody={t.t('chart.emptyBody')}
        />
      </section>

      <MilestonesStrip milestones={milestones} />

      <LeaderboardTile />

      <RecentActivityCard
        entries={activity}
        operatorNameByTenant={operatorNameByTenant}
        locale={t.locale}
      />

      <section style={cardStyle}>
        <header style={cardHeaderRow}>
          <h2 style={cardTitle}>{t.t('agent.home.shortcuts')}</h2>
        </header>
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <Link to="/earnings" style={primaryCta}>
            {t.t('agent.home.viewEarningsLedger')}
          </Link>
          <Link to="/operators" style={secondaryCta}>
            {t.t('agent.home.yourOperators')}
          </Link>
          <Link to="/code" style={secondaryCta}>
            {t.t('agent.home.yourShareCode')}
          </Link>
          {recruiterId ? (
            <Link to="/recruiter" style={secondaryCta}>
              {t.t('agent.home.recruiterDashboard')}
            </Link>
          ) : null}
        </div>
      </section>

      {recruiterId ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t.t('agent.home.recruiterNote')}
        </p>
      ) : null}

      <PwaInstallHint
        storageKey="kilat.install-hint.agent-pwa"
        subline={t.t('agent.home.installSubline')}
        labels={{
          headline: t.t('pwaInstall.headline', { app: agentAppName }),
          dialogLabel: t.t('pwaInstall.dialogLabel', { app: agentAppName }),
          install: t.t('pwaInstall.install'),
          installing: t.t('pwaInstall.installing'),
          notNow: t.t('pwaInstall.notNow'),
          dismiss: t.t('pwaInstall.dismiss'),
          shareIcon: t.t('pwaInstall.shareIcon'),
          iosStep1: t.t('pwaInstall.iosStep1'),
          iosStep2: t.t('pwaInstall.iosStep2'),
          iosStep3: t.t('pwaInstall.iosStep3'),
        }}
      />
    </Wrap>
  );
}

// ── components ─────────────────────────────────────────────────────────

function ForecastCard({
  forecast,
  activeOpCount,
  streak,
  lifetimeOpCount,
}: {
  readonly forecast: NextMonthForecast;
  readonly activeOpCount: number;
  readonly streak: number;
  readonly lifetimeOpCount: number;
}) {
  const t = useI18n();
  const confidenceWording = (() => {
    switch (forecast.confidence) {
      case 'high':
        return t.t('agent.home.forecastConfidenceHigh');
      case 'medium':
        return t.t('agent.home.forecastConfidenceMedium');
      case 'low':
        return t.t('agent.home.forecastConfidenceLow');
      case 'none':
        return t.t('agent.home.forecastConfidenceNone');
    }
  })();
  const hasData = forecast.confidence !== 'none';
  const signupsPerMonth = forecast.avgSignupsPerMonth;
  const perSignup = forecast.avgSenPerSignup;
  return (
    <section
      style={{
        background: 'linear-gradient(135deg, #0d7a4f 0%, #16956c 100%)',
        color: '#ffffff',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-2)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          opacity: 0.85,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        {t.t('agent.home.forecastEyebrow')}
      </p>
      <div
        style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <span
          style={{
            fontSize: 'var(--text-display)',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {hasData ? money.format(forecast.recurringSen) : '—'}
        </span>
        <span style={{ fontSize: 'var(--text-meta)', opacity: 0.85 }}>
          {hasData
            ? t.t('agent.home.forecastSublineData', {
                signups: signupsPerMonth.toFixed(1),
                each: money.format(perSignup),
              })
            : t.t('agent.home.forecastSublineNone')}
        </span>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          opacity: 0.85,
          lineHeight: 1.5,
        }}
      >
        {confidenceWording}.{' '}
        {hasData ? t.t('agent.home.forecastBodyData') : t.t('agent.home.forecastBodyNone')}
      </p>
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
          fontSize: 'var(--text-meta)',
          opacity: 0.92,
        }}
      >
        <span>
          <strong>{lifetimeOpCount}</strong> {t.t('agent.home.lifetimeSignups')}
        </span>
        <span>·</span>
        <span>
          <strong>{activeOpCount}</strong> {t.t('agent.home.active')}
        </span>
        {streak > 0 ? <span>·</span> : null}
        {streak > 0 ? (
          <span>
            <strong>{t.t('agent.home.nMonth', { n: streak })}</strong>{' '}
            {t.t('agent.home.signupStreak')}
          </span>
        ) : null}
      </div>
    </section>
  );
}

function BalanceTile({
  owedSen,
  payableSen,
  paidSen,
  lifetimeSen,
  isEmpty,
}: {
  readonly owedSen: number;
  readonly payableSen: number;
  readonly paidSen: number;
  readonly lifetimeSen: number;
  readonly isEmpty: boolean;
}) {
  const t = useI18n();
  return (
    <section style={cardStyle}>
      <header style={cardHeaderRow}>
        <h2 style={cardTitle}>{t.t('agent.home.balance.title')}</h2>
      </header>
      {isEmpty ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t.t('agent.home.balance.emptyBody')}
        </p>
      ) : (
        <>
          <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
              {t.t('agent.home.balance.owedLabel')}
            </span>
            <span
              style={{
                fontSize: 'var(--text-display)',
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--color-primary)',
              }}
            >
              {money.format(owedSen)}
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 120px), 1fr))',
              gap: 'var(--space-3)',
            }}
          >
            <BalanceStat
              label={t.t('agent.home.balance.payableLabel')}
              value={money.format(payableSen)}
            />
            <BalanceStat
              label={t.t('agent.home.balance.paidLabel')}
              value={money.format(paidSen)}
            />
            <BalanceStat
              label={t.t('agent.home.balance.lifetimeLabel')}
              value={money.format(lifetimeSen)}
            />
          </div>
        </>
      )}
    </section>
  );
}

function BalanceStat({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--space-1)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--tint-subtle)',
        border: '1px solid var(--color-border)',
      }}
    >
      <span
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.3,
        }}
      >
        {label}
      </span>
      <strong
        style={{
          fontSize: 'var(--text-body)',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </strong>
    </div>
  );
}

function RecentActivityCard({
  entries,
  operatorNameByTenant,
  locale,
}: {
  readonly entries: readonly EarningsLedgerEntry[];
  readonly operatorNameByTenant: ReadonlyMap<string, string>;
  readonly locale: string;
}) {
  const t = useI18n();
  return (
    <section style={cardStyle}>
      <header style={cardHeaderRow}>
        <h2 style={cardTitle}>{t.t('agent.home.activity.title')}</h2>
      </header>
      {entries.length === 0 ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t.t('agent.home.activity.empty')}
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          {entries.map((entry) => {
            const desc = describeLedgerEntry(entry);
            const baseLabel = t.t(desc.labelKey);
            const operatorName =
              entry.kind === 'accrued' ? operatorNameByTenant.get(entry.tenantId) : undefined;
            const label = operatorName
              ? t.t('agent.home.activity.withOperator', {
                  label: baseLabel,
                  operator: operatorName,
                })
              : baseLabel;
            return (
              <li
                key={entry.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--tint-subtle)',
                  border: '1px solid var(--color-border)',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontWeight: 500, fontSize: 'var(--text-meta)' }}>
                    {label}
                  </p>
                  <p
                    style={{
                      margin: 'var(--space-1) 0 0 0',
                      fontSize: 'var(--text-meta)',
                      color: 'var(--color-on-surface-muted)',
                    }}
                  >
                    {formatActivityDate(accruedAtMs(entry), locale)}
                  </p>
                </div>
                <strong
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                    color: desc.sign > 0 ? 'var(--color-primary)' : 'var(--color-on-surface-muted)',
                  }}
                >
                  {desc.sign > 0 ? '+' : ''}
                  {money.format(entry.amount)}
                </strong>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * Display-only date for a ledger row. `ms` comes from an already-STORED
 * `accruedAt` Timestamp (read → epoch ms via `accruedAtMs`); rendering it as a
 * `Date` here is presentation, not a stored value, so it doesn't break the
 * "no `new Date()` for stored values" rule. `0` (unrecognised) → an em dash.
 */
function formatActivityDate(ms: number, locale: string): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function MilestonesStrip({ milestones }: { readonly milestones: readonly Milestone[] }) {
  const t = useI18n();
  const unlocked = milestones.filter((m) => m.unlocked);
  const nextUp = milestones.filter((m) => !m.unlocked).sort((a, b) => b.progress - a.progress);
  const featured = nextUp[0]; // closest pending milestone — show first

  return (
    <section style={cardStyle}>
      <header style={cardHeaderRow}>
        <h2 style={cardTitle}>{t.t('agent.home.milestones')}</h2>
        <span style={cardHint}>
          {t.t('agent.home.milestonesUnlocked', {
            unlocked: unlocked.length,
            total: milestones.length,
          })}
        </span>
      </header>
      <ul
        style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-2)' }}
      >
        {featured ? <MilestoneRow milestone={featured} highlight /> : null}
        {unlocked.slice(0, 3).map((m) => (
          <MilestoneRow key={m.id} milestone={m} />
        ))}
      </ul>
    </section>
  );
}

function MilestoneRow({
  milestone,
  highlight,
}: {
  readonly milestone: Milestone;
  readonly highlight?: boolean;
}) {
  const t = useI18n();
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: 'var(--space-3)',
        alignItems: 'center',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        background: milestone.unlocked
          ? 'rgba(13,122,79,0.06)'
          : highlight
            ? 'rgba(245,158,11,0.06)'
            : 'var(--tint-subtle)',
        border: milestone.unlocked
          ? '1px solid rgba(13,122,79,0.22)'
          : highlight
            ? '1px solid rgba(245,158,11,0.30)'
            : '1px solid var(--color-border)',
      }}
    >
      <ProgressRing
        fraction={milestone.unlocked ? 1 : milestone.progress}
        unlocked={milestone.unlocked}
      />
      <div>
        <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-meta)' }}>
          {t.t(milestone.titleKey, milestone.vars)}
          {!milestone.unlocked && highlight ? ` · ${t.t('agent.home.nextUp')}` : ''}
        </p>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.4,
          }}
        >
          {t.t(milestone.bodyKey, milestone.vars)}
        </p>
      </div>
    </li>
  );
}

function ProgressRing({
  fraction,
  unlocked,
}: {
  readonly fraction: number;
  readonly unlocked: boolean;
}) {
  const r = 16;
  const cx = 20;
  const cy = 20;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.max(0, Math.min(1, fraction)));
  const color = unlocked ? 'var(--color-primary)' : '#f59e0b';
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden focusable="false">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--color-border)" strokeWidth={3} />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={3}
        strokeLinecap="round"
        strokeDasharray={`${circ} ${circ}`}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: 'stroke-dashoffset 0.4s ease' }}
      />
      {unlocked ? (
        <path
          d="M14 20 L18 24 L26 16"
          stroke={color}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      ) : null}
    </svg>
  );
}

// ── shell ───────────────────────────────────────────────────────────────

/** `dash`: the signed-in dashboard, whose cards go two across on a laptop. */
function Wrap({ children, dash }: { readonly children: React.ReactNode; readonly dash?: boolean }) {
  return (
    <main
      className={dash ? 'ag-page ag-dash' : 'ag-page'}
      style={{
        minHeight: '100dvh',
        padding: 'var(--space-5) var(--space-4) var(--space-12) var(--space-4)',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
      }}
    >
      {children}
    </main>
  );
}

const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  boxShadow: 'var(--shadow-1)',
  display: 'grid',
  gap: 'var(--space-3)',
};

const cardHeaderRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 'var(--space-2)',
  flexWrap: 'wrap',
};

const cardTitle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-body)',
  fontWeight: 600,
};

const cardHint: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const metaEyebrow: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  letterSpacing: 4,
  textTransform: 'uppercase',
};

const primaryCta: React.CSSProperties = {
  display: 'inline-block',
  padding: '0.5em 1em',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 'var(--text-meta)',
  minHeight: 'var(--touch-target)',
};

const secondaryCta: React.CSSProperties = {
  display: 'inline-block',
  padding: '0.5em 1em',
  borderRadius: 'var(--radius-pill)',
  background: 'transparent',
  color: 'var(--color-on-surface)',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 'var(--text-meta)',
  border: '1px solid var(--color-border-strong)',
  minHeight: 'var(--touch-target)',
};
